import { useForm } from "react-hook-form"
import { Button } from "~/ui/button"
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "~/ui/form"
import { Input } from "~/ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "~/ui/select"
import { PlacesAutocomplete } from "~/ui/places-autocomplete"
import { LocationStatus } from "~/__generated__/graphql"

interface LocationFormData {
  name: string
  status: LocationStatus
  websiteUrl?: string
  googleMapsUrl?: string
  latitude?: number
  longitude?: number
  streetAddress?: string
  streetAddress2?: string
  city?: string
  stateProvince?: string
  postalCode?: string
  country?: string
  formattedAddress?: string
  placeId?: string
  administrativeAreaLevel1?: string
  administrativeAreaLevel2?: string
  neighborhood?: string
  phone?: string
  timeZone?: string
}

interface LocationFormProps {
  defaultValues?: LocationFormData
  onSubmit: (data: LocationFormData) => void
  mode: "create" | "edit"
  isSubmitting?: boolean
}

export const LocationForm = ({
  defaultValues,
  onSubmit,
  mode,
  isSubmitting,
}: LocationFormProps) => {
  const form = useForm<LocationFormData>({
    defaultValues: defaultValues || {
      name: "",
      status: LocationStatus.Pending,
    },
  })

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <input type="hidden" {...form.register("latitude")} />
        <input type="hidden" {...form.register("longitude")} />
        <input type="hidden" {...form.register("streetAddress")} />
        <input type="hidden" {...form.register("streetAddress2")} />
        <input type="hidden" {...form.register("city")} />
        <input type="hidden" {...form.register("stateProvince")} />
        <input type="hidden" {...form.register("postalCode")} />
        <input type="hidden" {...form.register("country")} />
        <input type="hidden" {...form.register("formattedAddress")} />
        <input type="hidden" {...form.register("placeId")} />
        <input type="hidden" {...form.register("administrativeAreaLevel1")} />
        <input type="hidden" {...form.register("administrativeAreaLevel2")} />
        <input type="hidden" {...form.register("neighborhood")} />
        <input type="hidden" {...form.register("name")} />
        <input type="hidden" {...form.register("websiteUrl")} />
        <input type="hidden" {...form.register("googleMapsUrl")} />
        <input type="hidden" {...form.register("phone")} />
        <input type="hidden" {...form.register("timeZone")} />

        {mode === "create" && (
          <FormItem>
            <FormLabel>Address</FormLabel>
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="flex-grow">
                  <PlacesAutocomplete
                    includeBusinesses={true}
                    onLocationSelect={(lat, lng, addressData) => {
                      console.log("PlacesAutocomplete raw addressData:", addressData)
                      form.setValue("name", addressData.name || "")
                      form.setValue("websiteUrl", addressData.websiteUrl || undefined)
                      form.setValue("googleMapsUrl", addressData.googleMapsUrl || undefined)
                      form.setValue("phone", addressData.phone || undefined)
                      form.setValue("latitude", lat)
                      form.setValue("longitude", lng)
                      form.setValue("streetAddress", addressData.streetAddress)
                      form.setValue("streetAddress2", addressData.streetAddress2 || undefined)
                      form.setValue("city", addressData.city || undefined)
                      form.setValue("stateProvince", addressData.stateProvince || undefined)
                      form.setValue("postalCode", addressData.postalCode || undefined)
                      form.setValue("country", addressData.country || undefined)
                      form.setValue("formattedAddress", addressData.formattedAddress)
                      form.setValue("placeId", addressData.placeId)
                      form.setValue(
                        "administrativeAreaLevel1",
                        addressData.administrativeAreaLevel1 || undefined
                      )
                      form.setValue(
                        "administrativeAreaLevel2",
                        addressData.administrativeAreaLevel2 || undefined
                      )
                      form.setValue("neighborhood", addressData.neighborhood || undefined)
                      form.setValue("timeZone", addressData.timeZone || undefined)
                      console.log("Form values after set:", form.getValues())
                    }}
                    className="w-full"
                  />
                </div>
              </div>
              {form.watch("formattedAddress") && (
                <div className="text-sm text-gray-600">{form.watch("formattedAddress")}</div>
              )}
            </div>
          </FormItem>
        )}

        {mode === "edit" && (
          <>
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Location name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="streetAddress"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Street Address</FormLabel>
                  <FormControl>
                    <Input placeholder="123 Main St" {...field} value={field.value ?? ""} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="streetAddress2"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Street Address 2</FormLabel>
                  <FormControl>
                    <Input placeholder="Apt 4B" {...field} value={field.value ?? ""} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="city"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>City</FormLabel>
                  <FormControl>
                    <Input placeholder="City" {...field} value={field.value ?? ""} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="stateProvince"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>State/Province</FormLabel>
                  <FormControl>
                    <Input placeholder="State/Province" {...field} value={field.value ?? ""} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="postalCode"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Postal Code</FormLabel>
                  <FormControl>
                    <Input placeholder="Postal Code" {...field} value={field.value ?? ""} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="country"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Country</FormLabel>
                  <FormControl>
                    <Input placeholder="Country" {...field} value={field.value ?? ""} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="status"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Status</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select status" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {Object.values(LocationStatus).map((status) => (
                        <SelectItem key={status} value={status}>
                          {status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Phone</FormLabel>
                  <FormControl>
                    <Input placeholder="Phone number" {...field} value={field.value ?? ""} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="websiteUrl"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Website URL
                    {form.watch("websiteUrl") && (
                      <a
                        href={form.watch("websiteUrl")}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ml-2 text-sm font-normal text-blue-600 hover:underline"
                      >
                        (Visit)
                      </a>
                    )}
                  </FormLabel>
                  <FormControl>
                    <Input placeholder="https://example.com" {...field} value={field.value ?? ""} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="googleMapsUrl"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Google Maps URL
                    {form.watch("googleMapsUrl") && (
                      <a
                        href={form.watch("googleMapsUrl")}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ml-2 text-sm font-normal text-blue-600 hover:underline"
                      >
                        (Visit)
                      </a>
                    )}
                  </FormLabel>
                  <FormControl>
                    <Input
                      placeholder="https://maps.google.com/..."
                      {...field}
                      value={field.value ?? ""}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </>
        )}

        <div className="flex justify-end">
          <Button type="submit" disabled={isSubmitting}>
            Save Changes
          </Button>
        </div>
      </form>
    </Form>
  )
}
